/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.ui.comp

import io.peekandpoke.kraft.addons.styling.RawStyleSheet
import io.peekandpoke.kraft.addons.styling.StyleSheet
import kotlinx.css.CssBuilder
import kotlin.random.Random
import kotlinx.browser.document
import org.w3c.dom.HTMLElement

/**
 * The classes of the Klang controls (KLANG-00337): lamps, buttons, the riveted panel.
 *
 * Every rule reads the look's roles as `var(--kl-...)` ([io.peekandpoke.klang.ui.feel.KlangRole]) and nothing
 * else, so a changed role repaints without a re-render. Derived shades are mixed here from the roles.
 *
 * One light from above (KLANG-00207): a raised thing has a highlight on top and a shadow below, a pressed thing an
 * inset shadow, a printed label neither.
 *
 * The order of the rules is part of the design: a later rule of one class wins over an earlier one, so the finishes
 * come first, then the shapes, then pressed, disabled and later, then the beacon.
 */
object KlangControlStyles : StyleSheet("kl-controls") {

    /** The grain tile's side. */
    const val GRAIN_TILE_PX = 160

    /** A tool window's width, the same flown in and docked. */
    const val TOOL_WINDOW_WIDTH_PX = 340

    /** A tile of grey noise, opaque; the [grain] layer lays it over a surface at the look's strength. */
    private val GRAIN = grainImage(seed = 0)

    /**
     * The grain tile grown from [seed], as a CSS image: the user's own noise (the maintainer, 2026-10-28: "every user
     * should get a ui-seed"); the app puts it on the root as `--kl-grain-image` ([installGrain]).
     */
    fun grainImage(seed: Int): String =
        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' " +
            "width='$GRAIN_TILE_PX' height='$GRAIN_TILE_PX' viewBox='0 0 $GRAIN_TILE_PX $GRAIN_TILE_PX'>" +
            "<filter id='g'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' " +
            "seed='${seed and 0xffff}' stitchTiles='stitch'/>" +
            "<feColorMatrix values='1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 0 0 0 0 1'/></filter>" +
            "<rect width='100%' height='100%' filter='url(%23g)'/></svg>\")"

    /** The user's grain, from their [seed], on the document's root; until then the grain of seed 0. */
    fun installGrain(seed: Int) {
        (document.documentElement as? HTMLElement)?.style?.setProperty("--kl-grain-image", grainImage(seed))
    }

    /**
     * A fine grain over a surface, so a cap or a button reads as machined metal, not as smooth plastic (the
     * maintainer, 2026-10-26: "it could use a little noise to give it texture"; "the buttons could use the same noise
     * ... maybe this noise should be a theme property"): a layer of [GRAIN] under the surface's content, blended in
     * `overlay` at the look's `--kl-grain` ([io.peekandpoke.klang.ui.feel.KlangRole.Grain]), so it adds grain and keeps
     * the look's colours. The surface must be positioned (the buttons are relative, the knob's cap absolute); it
     * becomes its own stacking context, so the layer lies over its background and under its text and marks.
     *
     * Each surface may cut its grain from another place of the tile, `--kl-grain-x` and `--kl-grain-y` ([grainOffset]),
     * so no two buttons look alike (the maintainer, 2026-10-28: "a random offset for the grain in x an y so that they
     * do not like identical").
     */
    private fun CssBuilder.grain() {
        put("isolation", "isolate")

        rule("&::before") {
            put("content", "\"\"")
            put("position", "absolute")
            put("inset", "0")
            put("z-index", "-1")
            put("border-radius", "inherit")
            put("pointer-events", "none")
            put("background-image", "var(--kl-grain-image, $GRAIN)")
            put("background-position", "var(--kl-grain-x, 0px) var(--kl-grain-y, 0px)")
            // The tile scaled to the look's grain size: the noise is a vector, so a larger tile is coarser grain
            put("background-size", "calc(80px + var(--kl-grain-size) * 320px)")
            put("mix-blend-mode", "overlay")
            put("opacity", "var(--kl-grain)")
        }
    }

    /** A place to cut a surface's grain from, at random: a surface keeps its own while it lives. */
    fun grainOffset(): Pair<Int, Int> = Random.nextInt(GRAIN_TILE_PX) to Random.nextInt(GRAIN_TILE_PX)

    /** [offset] as the grain's cut ([grainOffset]), in an element's inline style. */
    fun CssBuilder.grainAt(offset: Pair<Int, Int>) {
        put("--kl-grain-x", "-${offset.first}px")
        put("--kl-grain-y", "-${offset.second}px")
    }

    /**
     * Brushed metal on the bench's frame (KLANG-00366; the maintainer, 2026-10-27: "the brushed iron look"; then "maybe
     * the inner content on this page should not be brushed only the sides"): the column, the strip and the tool
     * windows have it, quietly; the middle, where the page's own content is, stays plain. The tile
     * of [io.peekandpoke.klang.ui.feel.KlangBrushedMetal] under the part's content, at the look's `--kl-brushed`, so
     * the part keeps the look's ground and gains the strokes. The part becomes positioned and its own stacking
     * context; until the tile is painted it shows plain.
     */
    private fun CssBuilder.brushed() {
        put("position", "relative")
        put("isolation", "isolate")

        rule("&::before") {
            put("content", "\"\"")
            put("position", "absolute")
            put("inset", "0")
            put("z-index", "-1")
            put("border-radius", "inherit")
            put("pointer-events", "none")
            put("background-image", "var(--kl-brushed-image, none)")
            put("background-repeat", "repeat")
            put("opacity", "var(--kl-brushed)")
        }
    }

    /** A patch of the frame's ground with its brushed finish: the look's `--kl-brushed` shown on its own. */
    val brushedSwatch by rule {
        put("width", "120px")
        put("height", "40px")
        put("border", "1px solid var(--kl-edge)")
        put("border-radius", "3px")
        put("background", "var(--kl-ground-deep)")

        brushed()
    }

    /**
     * The way back ([KlangBackLink]): a pill with a thin outline, a chevron and the place's name, brighter under the
     * mouse. `!important` on the colour, as the app's global link rule paints every link gold so.
     */
    val backLink by rule {
        put("display", "inline-flex")
        put("align-items", "center")
        put("align-self", "flex-start")
        put("gap", "4px")
        put("padding", "4px 12px 4px 6px")
        put("border", "1px solid color-mix(in srgb, var(--kl-ink-label) 45%, transparent)")
        put("border-radius", "999px")
        put("font-size", "13px")
        put("line-height", "1.3")
        put("color", "var(--kl-ink-text) !important")
        put("text-decoration", "none")
        put("transition", "border-color 120ms ease, color 120ms ease")

        rule("&:hover") {
            put("color", "var(--kl-ink-strong) !important")
            put("border-color", "var(--kl-ink-label)")
        }

        rule("&:focus-visible") {
            put("outline", "2px solid var(--kl-focus)")
            put("outline-offset", "2px")
        }

        rule("& > i.icon") {
            put("margin", "0")
        }
    }

    // ── Lamps ─────────────────────────────────────────────────────────────────

    val lamp by rule {
        put("display", "inline-block")
        put("flex-shrink", "0")
        put("border-radius", "50%")
        put("background", "var(--kl-lamp-off)")
        put("box-shadow", "inset 0 1px 1px rgba(0,0,0,0.6)")
    }

    val lampOn by rule {
        put("background", "var(--kl-lamp-on)")
        put("box-shadow", "0 0 6px var(--kl-lamp-on)")
    }

    val lampTry by rule {
        put("background", "var(--kl-lamp-try)")
        put("box-shadow", "0 0 6px var(--kl-lamp-try)")
        put("animation", "kl-lamp-pulse 1.8s ease-in-out infinite")
    }

    val lampWarning by rule {
        put("background", "var(--kl-lamp-warning)")
        put("box-shadow", "0 0 6px var(--kl-lamp-warning)")
    }

    val lampCritical by rule {
        put("background", "var(--kl-lamp-critical)")
        put("box-shadow", "0 0 6px var(--kl-lamp-critical)")
    }

    /** No glass, only its ring: a lamp of something not reached yet ([KlangLamp.State.Unlit]). */
    val lampUnlit by rule {
        put("background", "transparent")
        put("box-shadow", "inset 0 0 0 1px color-mix(in srgb, var(--kl-ink-label) 45%, transparent)")
    }

    // ── Buttons: the base ─────────────────────────────────────────────────────

    val button by rule {
        // Positioned: the finish's grain layer takes the button's box (see grain())
        put("position", "relative")
        put("display", "inline-flex")
        put("align-items", "center")
        put("justify-content", "center")
        put("gap", "8px")
        put("box-sizing", "border-box")
        put("height", "34px")
        put("padding", "0 14px")
        put("margin", "0")
        put("border", "1px solid var(--kl-edge)")
        put("border-radius", "var(--kl-radius)")
        put("font-family", "inherit")
        put("font-size", "13px")
        put("font-weight", "700")
        put("line-height", "1")
        put("white-space", "nowrap")
        put("cursor", "pointer")
        put("transition", "filter 120ms ease, box-shadow 120ms ease")

        rule("&:hover") {
            put("filter", "brightness(1.08)")
        }

        rule("&:focus-visible") {
            put("outline", "2px solid var(--kl-focus)")
            put("outline-offset", "2px")
        }

        // Fomantic gives every icon a right margin; on a button that would push it off centre
        rule("& > i.icon") {
            put("margin", "0")
        }
    }

    // ── Buttons: finish and surface ───────────────────────────────────────────

    val darkFlat by rule {
        put("color", "var(--kl-ink-strong)")
        put("background", "linear-gradient(180deg, var(--kl-cap-light), var(--kl-cap-dark))")
        put("box-shadow", "0 2px 3px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.1)")
        grain()
    }

    val darkDomed by rule {
        put("color", "var(--kl-ink-strong)")
        put("border-radius", "var(--kl-radius-domed)")
        put(
            "background",
            "radial-gradient(ellipse 120% 90% at 50% 10%, var(--kl-cap-light) 0%," +
                    " color-mix(in srgb, var(--kl-cap-light), var(--kl-cap-dark)) 55%, var(--kl-cap-dark) 100%)",
        )
        grain()
        put("box-shadow", "0 3px 5px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.18)")
    }

    val silverFlat by rule {
        put("color", "var(--kl-metal-ink)")
        put(
            "background",
            "linear-gradient(180deg, var(--kl-metal-light) 0%, var(--kl-metal-mid) 48%," +
                    " color-mix(in srgb, var(--kl-metal-mid), var(--kl-metal-dark) 30%) 52%," +
                    " color-mix(in srgb, var(--kl-metal-light), var(--kl-metal-mid)) 100%)"
        )
        put("box-shadow", "0 2px 4px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.9)")
        grain()
    }

    val silverDomed by rule {
        put("color", "var(--kl-metal-ink)")
        put("border-radius", "var(--kl-radius-domed)")
        put(
            "background",
            "radial-gradient(ellipse 120% 90% at 45% 15%, var(--kl-metal-light) 0%, var(--kl-metal-mid) 50%," +
                    " var(--kl-metal-dark) 100%)",
        )
        grain()
        put("box-shadow", "0 3px 5px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.7)")
    }

    // ── Buttons: shape ────────────────────────────────────────────────────────

    val pill by rule {
        put("border-radius", "999px")
    }

    val round by rule {
        put("width", "34px")
        put("padding", "0")
        put("border-radius", "50%")
        // A circle stays a circle in a crowded header row, never squeezed into an oval
        put("flex-shrink", "0")
    }

    /** A round button at a header's icon size, beside a [KlangIconToggle] ([KlangCloseButton.Size.Small]). */
    val roundSmall by rule {
        put("width", "28px")
        put("height", "28px")
        put("font-size", "12px")
    }

    /**
     * The close's flat finish (the maintainer, 2026-10-27: "Can we make the KlangCloseButton flat please", then of its
     * thin ring: "Maybe not even the circle"): only the x, in the label's ink, brighter under the mouse; the button's
     * box stays, so it is as easy to hit. After the finishes, so it wins over them.
     */
    val closeFlat by rule {
        put("color", "var(--kl-ink-label)")
        put("background", "transparent")
        put("border-color", "transparent")
        put("box-shadow", "none")

        rule("&:hover") {
            put("color", "var(--kl-ink-strong)")
        }
    }

    /**
     * A tall button: a drawer's entry, a swatch button. Its lamp sits at the top left. Its text starts at the top, also
     * when a neighbour's longer text makes the row taller (the maintainer, 2026-10-26).
     */
    val tile by rule {
        put("position", "relative")
        put("height", "auto")
        put("min-height", "52px")
        put("flex-direction", "column")
        put("justify-content", "flex-start")
        put("align-items", "center")
        put("gap", "4px")
        put("padding", "8px 10px")
        put("white-space", "normal")
        put("line-height", "1.2")

        rule("& > span[data-lamp]") {
            put("position", "absolute")
            put("left", "10px")
            put("top", "13px")
        }
    }

    /** A tile with a lamp leaves room for it on the left. */
    val tileWithLamp by rule {
        put("padding-left", "24px")
        put("align-items", "flex-start")
        put("text-align", "left")
    }

    // ── Buttons: state ────────────────────────────────────────────────────────

    val pressedDark by rule {
        put(
            "background",
            "linear-gradient(180deg, var(--kl-cap-dark), color-mix(in srgb, var(--kl-cap-dark), var(--kl-cap-light) 25%))"
        )
        put("box-shadow", "inset 0 2px 5px rgba(0,0,0,0.8)")
    }

    val pressedSilver by rule {
        put("background", "linear-gradient(180deg, var(--kl-metal-dark), var(--kl-metal-mid))")
        put("box-shadow", "inset 0 2px 5px rgba(0,0,0,0.6)")
    }

    val disabled by rule {
        put("opacity", "0.45")
        put("cursor", "default")
        put("box-shadow", "none")

        rule("&:hover") {
            put("filter", "none")
        }
    }

    /** Not there yet ("with the backend"): a dashed outline where the button will be, nothing to press. */
    val later by rule {
        put("color", "var(--kl-ink-label)")
        put("background", "transparent")
        put("border", "1px dashed var(--kl-ink-label)")
        put("box-shadow", "none")
        put("cursor", "default")

        rule("&:hover") {
            put("filter", "none")
        }

        // No grain inside the outline: with no surface under it the grain would show as a grey haze (see grain())
        rule("&::before") {
            put("display", "none")
        }
    }

    /**
     * "Try me": the trail's pulse around a control (KLANG-00334, the first-visit trail). The pulse is a ring of its
     * own (`::after`), so the cap keeps its own shadows, raised or pressed.
     */
    val beacon by rule {
        put("position", "relative")

        rule("&::after") {
            put("content", "\"\"")
            put("position", "absolute")
            put("inset", "-1px")
            put("border-radius", "inherit")
            put("pointer-events", "none")
            put("animation", "kl-beacon 1.8s ease-in-out infinite")
        }
    }

    // ── The riveted panel ─────────────────────────────────────────────────────

    val panel by rule {
        put("position", "relative")
        put("box-sizing", "border-box")
        put("padding", "20px 18px 18px")
        put("color", "var(--kl-ink-text)")
        put(
            "background",
            "linear-gradient(180deg, color-mix(in srgb, var(--kl-ground-panel), white 6%) 0%, var(--kl-ground-panel) 40%," +
                    " color-mix(in srgb, var(--kl-ground-panel), black 15%) 100%)"
        )
        put("border", "1px solid var(--kl-edge)")
        put("border-radius", "4px")
        put("box-shadow", "0 14px 40px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.09)")
    }

    /** A panel holding a list (a menu): tighter, its entries run to the edges. */
    val panelTight by rule {
        put("padding", "14px 0 12px")
    }

    val rivet by rule {
        put("position", "absolute")
        put("width", "6px")
        put("height", "6px")
        put("border-radius", "50%")
        put("background", "radial-gradient(circle at 35% 35%, var(--kl-metal-dark), var(--kl-cap-dark))")
        put("pointer-events", "none")
    }

    val rivetTopLeft by rule {
        put("top", "6px")
        put("left", "6px")
    }

    val rivetTopRight by rule {
        put("top", "6px")
        put("right", "6px")
    }

    val rivetBottomLeft by rule {
        put("bottom", "6px")
        put("left", "6px")
    }

    val rivetBottomRight by rule {
        put("bottom", "6px")
        put("right", "6px")
    }

    // ── Card and caption ──────────────────────────────────────────────────────

    /** A flat plate on the ground: no rivets (rivets mean a popup), no shadow of its own. */
    val card by rule {
        put("display", "flex")
        put("flex-direction", "column")
        put("gap", "12px")
        put("box-sizing", "border-box")
        put("padding", "18px")
        put("color", "var(--kl-ink-text)")
        put("background", "var(--kl-ground-app)")
        put("border", "1px solid var(--kl-edge)")
        put("border-radius", "4px")
    }

    val captionTitle by rule {
        put("font-size", "13px")
        put("font-weight", "700")
        put("color", "var(--kl-ink-strong)")
    }

    val captionNote by rule {
        put("font-family", "monospace")
        put("font-size", "11px")
        put("color", "var(--kl-ink-label)")
    }

    /** A silkscreen heading: small capitals, spaced, printed on the panel. */
    val heading by rule {
        put("font-family", "monospace")
        put("font-size", "11px")
        put("letter-spacing", "0.18em")
        put("text-transform", "uppercase")
        put("color", "var(--kl-ink-label)")
    }

    // ── Swatch, search field, key cap ─────────────────────────────────────────

    val swatch by rule {
        put("width", "16px")
        put("height", "16px")
        put("flex-shrink", "0")
        put("border-radius", "50%")
        put("border", "1px solid var(--kl-edge)")
    }

    /** A swatch where there is none yet ("Custom" before it exists). */
    val swatchEmpty by rule {
        put("border", "1px dashed var(--kl-ink-label)")
    }

    /** A field cut into the panel: the search icon, the input, a key cap. */
    val searchField by rule {
        put("display", "flex")
        put("align-items", "center")
        put("gap", "8px")
        put("box-sizing", "border-box")
        put("height", "36px")
        put("padding", "0 10px")
        put("color", "var(--kl-ink-label)")
        put("background", "var(--kl-ground-sunken)")
        put("border", "1px solid var(--kl-edge)")
        put("border-radius", "var(--kl-radius)")
        put("box-shadow", "inset 0 2px 4px rgba(0,0,0,0.7)")

        rule("&:focus-within") {
            put("box-shadow", "inset 0 2px 4px rgba(0,0,0,0.7), 0 0 0 1.5px var(--kl-focus)")
        }
    }

    val searchInput by rule {
        put("flex", "1")
        put("min-width", "0")
        put("background", "transparent")
        put("border", "none")
        put("outline", "none")
        put("color", "var(--kl-ink-text)")
        put("font-family", "inherit")
        put("font-size", "13px")
    }

    /** A key cap, printed: the hint of a key that does the same. */
    val keyCap by rule {
        put("font-family", "monospace")
        put("font-size", "10px")
        put("line-height", "1")
        put("padding", "2px 5px")
        put("color", "var(--kl-ink-label)")
        put("border", "1px solid color-mix(in srgb, var(--kl-ink-label) 40%, transparent)")
        put("border-bottom-width", "2px")
        put("border-radius", "3px")
        put("white-space", "nowrap")
    }

    // ── Drawer: the Motor's menu (KLANG-00403) ──────────────────────────────────

    /** The title, the search field taking the room between, the close key and button at the end. */
    val drawerHeader by rule {
        put("display", "flex")
        put("align-items", "center")
        put("gap", "20px")
        put("margin-bottom", "18px")
    }

    /** The header's right end: the close key's pill beside the close button. */
    val drawerHeaderEnd by rule {
        put("display", "inline-flex")
        put("align-items", "center")
        put("margin-left", "auto")
    }

    /** The search field in the header, as wide as the header leaves it. */
    val drawerSearch by rule {
        put("flex", "1")
        put("min-width", "0")
    }

    /** The destinations: large tiles in four columns (the maintainer: "The button like 'Songs' can then also be bigger"). */
    val drawerGrid by rule {
        put("display", "grid")
        put("grid-template-columns", "repeat(4, minmax(0, 1fr))")
        put("gap", "14px")

        rule("& > button") {
            put("min-height", "96px")
            put("justify-content", "center")
            // The lamp sits in the text, before the label; the icon gets more room at the right end (the maintainer,
            // 2026-11-02: "a bit more padding to the right of the button")
            put("padding", "14px 24px 14px 16px")
        }
    }

    /** A destination's face: its icon beside its label and second line. */
    val drawerTile by rule {
        put("display", "flex")
        put("align-items", "center")
        put("gap", "16px")
        put("width", "100%")

        // At the right end, the text taking the room before it; raised from the tile (the maintainer, 2026-11-02,
        // after trying it engraved: "Ok well maybe raised?"): lighter than the cap, its upper edge catching the light,
        // its shadow below
        rule("& > i.icon") {
            put("flex-shrink", "0")
            put("margin", "0 0 0 auto")
            put("font-size", "28px")
            put("width", "36px")
            put("color", "color-mix(in srgb, var(--kl-cap-light), white 22%)")
            put(
                "text-shadow",
                "0 -1px 0 color-mix(in srgb, var(--kl-cap-light), white 55%), " +
                        "0 1px 0 rgba(0, 0, 0, 0.55), 0 2px 4px rgba(0, 0, 0, 0.45)",
            )
        }
    }

    val drawerTileText by rule {
        put("display", "flex")
        put("flex-direction", "column")
        put("gap", "4px")
        put("min-width", "0")
        put("text-align", "left")
    }

    val drawerLabel by rule {
        put("font-size", "20px")
        put("font-weight", "700")
        put("line-height", "1.15")
    }

    /** A tile's lamp and its label in a row ([KlangDrawer.LAMP_PX], [KlangDrawer.LAMP_GAP_PX]). */
    val drawerTileHead by rule {
        put("display", "flex")
        put("align-items", "center")
        put("gap", "${KlangDrawer.LAMP_GAP_PX}px")
    }

    /** A tile's second line, under the label's text, past its lamp. */
    val drawerTileSub by rule {
        put("padding-left", "${KlangDrawer.LAMP_PX + KlangDrawer.LAMP_GAP_PX}px")
        put("font-size", "13px")
        put("font-weight", "400")
        put("color", "var(--kl-ink-label)")
    }

    /** A second line in the label's colour, small: a song's RPM on its tile. */
    val drawerSub by rule {
        put("font-size", "11px")
        put("font-weight", "400")
        put("color", "var(--kl-ink-label)")
    }

    /** Under the destinations: what one comes back to, and the loadouts; one alone takes the width. */
    val drawerBelow by rule {
        put("display", "grid")
        put("grid-template-columns", "minmax(0, 1fr)")
        put("gap", "20px")
        put("margin-top", "20px")
    }

    /** Both halves: the left one a little wider. */
    val drawerBelowSplit by rule {
        put("grid-template-columns", "minmax(0, 7fr) minmax(0, 5fr)")
    }

    /** One half under the destinations, sunk into the panel. */
    val drawerSection by rule {
        put("padding", "16px 18px")
        put("border-radius", "4px")
        put("background", "color-mix(in srgb, var(--kl-ground-deep) 45%, transparent)")
        put("border", "1px solid var(--kl-edge)")
    }

    /** A small caption over a group in a half: "Last opened songs", "Continue learning". */
    val drawerCaption by rule {
        put("display", "block")
        put("margin", "12px 0 6px")
        put("font-size", "12px")
        put("color", "var(--kl-ink-label)")

        // The first caption of a half opens it: no room above it
        rule("&:first-child") {
            put("margin-top", "0")
        }
    }

    /** What one comes back to: tiles in three columns, each a title over a small second line. */
    val drawerRecentGrid by rule {
        put("display", "grid")
        put("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        put("gap", "8px")

        rule("& > button") {
            put("min-height", "56px")
            put("align-items", "flex-start")
            put("justify-content", "center")
            put("padding", "8px 12px")
            put("text-align", "left")
        }
    }

    /** A title on a tile that may be long: one line, cut with an ellipsis. */
    val drawerRecentTitle by rule {
        put("display", "block")
        put("max-width", "100%")
        put("overflow", "hidden")
        put("white-space", "nowrap")
        put("text-overflow", "ellipsis")
        put("font-size", "14px")
        put("font-weight", "700")
    }

    /** The row of the song that plays: its tile takes the width, its stop key beside it as tall as the tile. */
    val drawerNowRow by rule {
        put("display", "grid")
        put("grid-template-columns", "minmax(0, 1fr) auto")
        put("gap", "8px")

        rule("& > button") {
            put("min-height", "56px")
            put("align-items", "flex-start")
            put("justify-content", "center")
            put("padding", "8px 12px")
            put("text-align", "left")
        }

        rule("& > button:last-child") {
            put("width", "56px")
            put("align-items", "center")
        }
    }

    /** The row holding the lesson tile, which takes its whole width. */
    val drawerLessonRow by rule {
        put("display", "grid")

        rule("& > button") {
            put("padding", "8px 12px")
        }
    }

    /**
     * The lesson tile: the track and its next lesson, the progress bar under them at the tile's whole width, inside its
     * padding (the maintainer, 2026-11-01: "below the text full width while respecting the padding of the button").
     */
    val drawerLesson by rule {
        put("display", "flex")
        put("flex-direction", "column")
        put("align-items", "stretch")
        put("gap", "8px")
        put("width", "100%")
        put("text-align", "left")
    }

    /** How far along a track is: a sunken bar, filled in the progress colour (the selected lamp's, unless a look sets it). */
    val drawerProgress by rule {
        put("display", "block")
        put("width", "100%")
        put("height", "8px")
        put("border-radius", "4px")
        put("overflow", "hidden")
        put("background", "var(--kl-ground-sunken)")

        rule("& > span") {
            put("display", "block")
            put("height", "100%")
            put("background", "var(--kl-progress)")
        }
    }

    /** The look picker's row: the presets as equal tiles. */
    val lookRow by rule {
        put("display", "grid")
        put("grid-template-columns", "repeat(4, minmax(0, 1fr))")
        // Every row as tall as one with looks in it: a row of empty slots alone would shrink to the "+"
        put("grid-auto-rows", "1fr")
        put("gap", "6px")
        put("margin-top", "8px")

        // Smaller tiles than a tile's own: seven looks fit in two short rows (the maintainer, 2026-10-28)
        rule("& > button") {
            put("min-height", "0")
            put("padding", "5px 4px")
            put("gap", "3px")
            put("font-size", "11px")
        }

        rule("& > button > span[aria-hidden]") {
            put("width", "12px")
            put("height", "12px")
        }

        // A name of the user's own may still be wider than a tile: cut with "...", whole in the editor (KLANG-00382)
        rule("& > button > span:last-child") {
            put("max-width", "100%")
            put("overflow", "hidden")
            put("text-overflow", "ellipsis")
            put("white-space", "nowrap")
        }
    }

    /** The LOOK heading with the Edit key at its right (KLANG-00382). */
    val lookHeading by rule {
        put("display", "flex")
        put("align-items", "center")
        put("justify-content", "space-between")
    }

    /**
     * The quiet key that opens the look editor: no frame, a pencil and a word in the label's ink, brighter on hover, as
     * the code players' copy key (the maintainer, 2026-10-28: "like the "copy to clipboard" style").
     */
    val quietKey by rule {
        put("display", "inline-flex")
        put("align-items", "center")
        put("gap", "5px")
        put("padding", "2px 4px")
        put("border", "none")
        put("border-radius", "var(--kl-radius)")
        put("background", "transparent")
        put("font-family", "inherit")
        put("font-size", "12px")
        put("color", "var(--kl-ink-label)")
        put("cursor", "pointer")

        rule("&:hover") {
            put("color", "var(--kl-ink-strong)")
        }

        rule("&:focus-visible") {
            put("outline", "2px solid var(--kl-focus)")
            put("outline-offset", "1px")
        }
    }

    /** An empty slot of the LOOK row: a quiet dashed "+" that opens the editor, that slot ready to be filled. */
    val lookEmpty by rule {
        put("display", "flex")
        put("align-items", "center")
        put("justify-content", "center")
        put("min-height", "38px")
        put("border", "1px dashed var(--kl-cap-light)")
        put("border-radius", "var(--kl-radius-domed)")
        put("background", "transparent")
        put("color", "var(--kl-ink-label)")
        put("font-size", "14px")
        put("cursor", "pointer")

        rule("&:hover") {
            put("color", "var(--kl-ink-strong)")
            put("border-color", "var(--kl-ink-label)")
        }

        rule("&:focus-visible") {
            put("outline", "2px solid var(--kl-focus)")
            put("outline-offset", "1px")
        }
    }

    // ── Dialog ────────────────────────────────────────────────────────────────

    /** The dialog's title, and its aside at the row's right end. */
    val dialogTitleRow by rule {
        put("display", "flex")
        put("align-items", "baseline")
        put("justify-content", "space-between")
        put("gap", "16px")
    }

    val dialogTitle by rule {
        put("font-size", "16px")
        put("font-weight", "700")
        put("color", "var(--kl-ink-strong)")
    }

    /** A line beside the dialog's title, in the text's voice. */
    val dialogAside by rule {
        put("font-size", "14px")
        put("color", "var(--kl-ink-text)")
    }

    val dialogBody by rule {
        put("margin-top", "8px")
        put("line-height", "20px")
    }

    /**
     * The key row at the panel's foot, edge to edge, as the button row of a device (maintainer, 2026-10-25): a sunken
     * strip, the keys sharing its width, so no key floats left or right of an empty space.
     */
    val dialogKeys by rule {
        put("display", "grid")
        put("grid-auto-columns", "minmax(0, 1fr)")
        put("grid-auto-flow", "column")
        put("gap", "10px")
        put("margin", "18px -18px -18px")
        put("padding", "12px 18px")
        put("background", "color-mix(in srgb, var(--kl-ground-sunken) 70%, transparent)")
        put("border-top", "1px solid var(--kl-edge)")
        // The panel's inner corner: its 4 px radius less its 1 px border
        put("border-radius", "0 0 3px 3px")
        put("box-shadow", "inset 0 2px 4px rgba(0,0,0,0.5)")
    }

    /** One key of the row: holds its button at the full width of its share; a long verb wraps inside it. */
    val dialogKey by rule {
        put("display", "flex")
        put("flex-direction", "column")
        put("align-items", "stretch")

        // On any focus, not only `:focus-visible`: a question opened with the mouse gives its main key the keyboard,
        // and Enter there answers it, so the ring shows which answer Enter gives (as the old modal's yes button did)
        rule("& > button:focus") {
            put("outline", "2px solid var(--kl-focus)")
            put("outline-offset", "2px")
        }

        rule("& > button") {
            put("height", "auto")
            put("min-height", "34px")
            put("padding-top", "6px")
            put("padding-bottom", "6px")
            put("white-space", "normal")
            put("text-align", "center")
            put("line-height", "1.2")
        }
    }

    // ── Menu ──────────────────────────────────────────────────────────────────

    val menuItem by rule {
        put("display", "flex")
        put("align-items", "center")
        put("justify-content", "space-between")
        put("gap", "16px")
        put("width", "100%")
        put("box-sizing", "border-box")
        put("padding", "7px 14px")
        put("border", "none")
        put("background", "transparent")
        put("color", "var(--kl-ink-text)")
        put("font-family", "inherit")
        put("font-size", "13px")
        put("text-align", "left")
        put("cursor", "pointer")
        put("outline", "none")

        rule("&:hover, &:focus") {
            put("background", "color-mix(in srgb, var(--kl-accent) 18%, transparent)")
            put("color", "var(--kl-ink-strong)")
        }

        rule("&:disabled") {
            put("color", "var(--kl-ink-label)")
            put("background", "transparent")
            put("cursor", "default")
        }
    }

    val menuHint by rule {
        put("font-family", "monospace")
        put("font-size", "11px")
        put("color", "var(--kl-ink-label)")
    }

    // ── Tool window ───────────────────────────────────────────────────────────

    /** Fills the place it is given (a fly-in slot, a docked column) along its parent's flex row. */
    val toolWindow by rule {
        put("display", "flex")
        put("flex-direction", "column")
        put("flex", "1")
        put("box-sizing", "border-box")
        put("min-width", "0")
        put("min-height", "0")
        put("color", "var(--kl-ink-text)")
        put("background", "var(--kl-ground-panel)")
        put("border-left", "1px solid var(--kl-edge)")

        brushed()
    }

    /** Flying in over the editor: a shadow to the left, rounded where it meets the editor. */
    val toolWindowFloating by rule {
        put("border-top-left-radius", "6px")
        put("box-shadow", "-18px 0 40px rgba(0,0,0,0.55), inset 1px 0 0 rgba(255,255,255,0.06)")
        put("animation", "kl-fly-in 0.18s ease-out")
    }

    /** Flying in from below: the shadow above it, rounded at its top corners. */
    val toolWindowFloatingBottom by rule {
        put("border-top-left-radius", "6px")
        put("border-top-right-radius", "6px")
        put("box-shadow", "0 -18px 40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)")
        put("animation", "kl-fly-up 0.18s ease-out")
    }

    val toolWindowHeader by rule {
        put("display", "flex")
        put("align-items", "center")
        put("gap", "8px")
        put("flex-shrink", "0")
        put("height", "44px")
        put("padding", "0 8px 0 16px")
        put("border-bottom", "1px solid var(--kl-ground-deep)")
    }

    val toolWindowTitle by rule {
        put("font-family", "monospace")
        put("font-size", "11px")
        put("letter-spacing", "0.18em")
        put("text-transform", "uppercase")
        put("color", "var(--kl-ink-strong)")
    }

    /** The tabs in a tool window's header, in its title's place: as tall as the header, each a quiet key. */
    val toolWindowTabs by rule {
        put("display", "flex")
        put("align-self", "stretch")
        put("gap", "4px")
        put("margin-left", "-8px")
    }

    val toolWindowTab by rule {
        put("display", "flex")
        put("align-items", "center")
        put("padding", "0 10px")
        put("border", "none")
        put("border-bottom", "2px solid transparent")
        put("background", "transparent")
        put("font-family", "monospace")
        put("font-size", "11px")
        put("letter-spacing", "0.18em")
        put("text-transform", "uppercase")
        put("color", "var(--kl-ink-label)")
        put("cursor", "pointer")

        rule("&:hover") {
            put("color", "var(--kl-ink-text)")
        }

        rule("&[aria-pressed=true]") {
            put("color", "var(--kl-ink-strong)")
            put("border-bottom-color", "var(--kl-accent)")
        }

        rule("&:focus-visible") {
            put("outline", "2px solid var(--kl-focus)")
            put("outline-offset", "-2px")
        }
    }

    val toolWindowChip by rule {
        put("font-size", "11px")
        put("color", "var(--kl-ink-label)")
        put("border", "1px solid color-mix(in srgb, var(--kl-ink-label) 40%, transparent)")
        put("border-radius", "10px")
        put("padding", "2px 8px")
        put("white-space", "nowrap")
    }

    /** A key's pill in a header, shown always where there is room ("Close: Esc", KLANG-00334 item 39): quiet, a label. */
    val keyPill by rule {
        put("display", "inline-flex")
        put("align-items", "center")
        put("gap", "5px")
        put("margin-right", "4px")
        put("font-size", "11px")
        put("color", "var(--kl-ink-label)")
        put("white-space", "nowrap")
    }

    /** A small icon button: a tool window's pin, the tool strip's toggles. Printed, not raised; on, it sinks in. */
    val iconToggle by rule {
        // Positioned: the count sits on its corner (iconToggleCount)
        put("position", "relative")
        put("display", "inline-flex")
        put("align-items", "center")
        put("justify-content", "center")
        put("width", "28px")
        put("height", "28px")
        put("padding", "0")
        put("border", "1px solid transparent")
        put("border-radius", "var(--kl-radius)")
        put("background", "transparent")
        put("color", "var(--kl-ink-label)")
        put("cursor", "pointer")

        rule("&:hover") {
            put("color", "var(--kl-ink-strong)")
        }

        rule("&:focus-visible") {
            put("outline", "none")
            put("box-shadow", "0 0 0 1.5px var(--kl-focus)")
        }

        rule("& > i.icon") {
            put("margin", "0")
        }
    }

    val iconToggleOn by rule {
        put("color", "var(--kl-lamp-on)")
        put("background", "var(--kl-ground-sunken)")
        put("border-color", "var(--kl-edge)")
        put("box-shadow", "inset 0 2px 4px rgba(0,0,0,0.8)")
    }

    /** The count on an icon toggle's corner ([KlangIconToggle.Props.count]): a small critical lamp with a number in it. */
    val iconToggleCount by rule {
        put("position", "absolute")
        put("top", "-4px")
        put("right", "-4px")
        COUNT_BADGE.forEach { (property, value) -> put(property, value) }
    }

    /**
     * The count after a tab's title ([KlangToolWindow.Tab.count]): the icon toggle's badge, in the line (the
     * maintainer, 2026-11-03: "show a red marker on the console, like on the icon on the right").
     */
    val toolWindowTabCount by rule {
        put("display", "inline-block")
        put("margin-left", "6px")
        put("letter-spacing", "0")
        COUNT_BADGE.forEach { (property, value) -> put(property, value) }
    }

    /** The tool strip's size of it. */
    val iconToggleLarge by rule {
        put("width", "36px")
        put("height", "36px")
        put("font-size", "16px")
    }

    val toolWindowBody by rule {
        put("flex", "1")
        put("min-height", "0")
        put("overflow", "auto")
        put("padding", "16px")
    }

    // ── The Motor's menu button ───────────────────────────────────────────────

    /** The nine dots on the menu button; while its drawer is open they are lit, the button pressed. */
    val menuButtonDots by rule {
        put("display", "block")
        put("fill", "currentColor")
    }

    val menuButtonOpen by rule {
        put("color", "var(--kl-lamp-on)")
    }

    /** The ring and the button inside it; it sits on the line's foot like the knob opposite. */
    val menuButtonFrame by rule {
        put("position", "relative")
        put("display", "inline-block")
        put("flex-shrink", "0")
        put("vertical-align", "bottom")

        // The focus ring inside the lamp ring, not on it: there it would read as the lamp lit
        rule("& > button:focus-visible") {
            put("outline-width", "1.5px")
            put("outline-offset", "0")
        }
    }

    /** The ring around the menu button: dark, a lamp that is off, while its drawer is closed. */
    val menuButtonRing by rule {
        put("display", "block")
        put("width", "100%")
        put("height", "100%")
        put("pointer-events", "none")
        put("fill", "none")
        put("stroke", "var(--kl-lamp-off)")
    }

    /** The ring lit, with a glow, while the drawer is open. */
    val menuButtonRingLit by rule {
        put("stroke", "var(--kl-lamp-on)")
        put("filter", "drop-shadow(0 0 3px var(--kl-lamp-on))")
    }

    // ── The ignition key ──────────────────────────────────────────────────────

    /** The key's box: the cap at its foot, the positions printed above it. */
    val ignition by rule {
        put("position", "relative")
        put("display", "inline-block")
        put("flex-shrink", "0")
    }

    /** The cap, a button: the knob's silver cap, without the button's own look. */
    val ignitionCap by rule {
        put("padding", "0")
        put("margin", "0")
        put("cursor", "pointer")
        put("appearance", "none")
        put("outline", "none")

        // A shadow ring, the cap's own shadows kept: Chrome draws a round outline jagged (as the knob found,
        // 2026-10-25); the transparent outline still shows in forced-colours modes, which drop shadows
        rule("&:focus-visible") {
            put("box-shadow", "0 3px 5px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.18), 0 0 0 3px var(--kl-focus)")
            put("outline", "1px solid transparent")
        }

        rule("&[aria-disabled=true]") {
            put("cursor", "default")
        }
    }

    /** The key's turn, a short motion with a little spring at its end. */
    val ignitionTurn by rule {
        put("transition", "transform 350ms cubic-bezier(0.3, 1.4, 0.5, 1)")
    }

    /** The key's grain turns with the same spring as the key. */
    val ignitionGrainTurn by rule {
        rule("&::before") {
            put("transition", "transform 350ms cubic-bezier(0.3, 1.4, 0.5, 1)")
        }
    }

    /** A position's name, centred on its spot; the glow lamp hangs after ON without moving it off centre. */
    val ignitionLabel by rule {
        put("position", "absolute")
        put("transform", "translate(-50%, -50%)")
        put("font-family", "monospace")
        put("line-height", "1")
        put("letter-spacing", "0.14em")
        put("white-space", "nowrap")
        put("user-select", "none")
        put("color", "var(--kl-ink-label)")

        rule("& > [data-lamp]") {
            put("position", "absolute")
            put("left", "calc(100% + 0.3em)")
            put("top", "50%")
            put("transform", "translateY(-50%)")
        }
    }

    /** The position the key is at. */
    val ignitionLabelCurrent by rule {
        put("font-weight", "700")
        put("color", "var(--kl-ink-strong)")
    }

    // ── For screen readers ────────────────────────────────────────────────────

    /** Text for screen readers only: in the page, out of sight (the usual clip pattern). */
    val visuallyHidden by rule {
        put("position", "absolute")
        put("width", "1px")
        put("height", "1px")
        put("margin", "-1px")
        put("padding", "0")
        put("overflow", "hidden")
        put("clip", "rect(0 0 0 0)")
        put("white-space", "nowrap")
        put("border", "0")
    }

    // ── The screen ────────────────────────────────────────────────────────────

    /** A screen set into the panel (KLANG-00349): fills its parent's flex column; its cut is `--kl-screen-cut`. */
    val screen by rule {
        put("position", "relative")
        put("display", "flex")
        put("flex-direction", "column")
        put("flex", "1 1 auto")
        put("min-height", "0")
    }

    /** The screen itself, in the panels' dark edge. */
    val screenSurface by rule {
        put("position", "relative")
        put("display", "flex")
        put("flex-direction", "column")
        put("flex", "1 1 auto")
        put("min-height", "0")
        put("border", "1px solid var(--kl-edge)")
    }

    /** Its depth: a shadow from the top edge, over the content, never in the way. */
    val screenShade by rule {
        put("position", "absolute")
        put("inset", "0")
        put("box-shadow", "inset 0 3px 8px rgba(0, 0, 0, 0.85), inset 0 0 2px rgba(0, 0, 0, 0.9)")
        put("pointer-events", "none")
        put("z-index", "5")
    }

    /**
     * A screen as an old tube (the song editor's): its text glows a little in its own colour, as lit phosphor, at the
     * look's `--kl-glow`; small, so the code stays sharp.
     */
    val screenTube by rule {
        rule("& .cm-content") {
            put("text-shadow", "0 0 4px color-mix(in srgb, currentColor calc(var(--kl-glow) * 100%), transparent)")
        }
    }

    /**
     * The tube's glass, over the content and never in the way: a faint reflection across the top, the corners and
     * edges darker, scan lines (a dark line every third pixel, at the look's `--kl-scanlines`), and the glass's own
     * grey-green, which lifts the black a little: on pure black, dark lines and a dark rim would not show.
     */
    val screenTubeGlass by rule {
        put("position", "absolute")
        put("inset", "0")
        put("pointer-events", "none")
        put("z-index", "5")
        put(
            "background",
            listOf(
                // The reflection and the rim kept to the corners and the edges: across the middle they looked like a
                // dirty monitor (the maintainer, 2026-10-28)
                "linear-gradient(160deg, rgba(255, 255, 255, 0.03) 0%, transparent 14%)",
                "radial-gradient(ellipse at center, transparent 70%, rgba(0, 0, 0, 0.3) 100%)",
                "repeating-linear-gradient(to bottom, rgba(0, 0, 0, var(--kl-scanlines)) 0 1px, transparent 1px 3px)",
                "rgba(150, 175, 165, 0.03)",
            ).joinToString(", "),
        )
    }

    /** What the screen shows: a flex column filling it. */
    val screenContent by rule {
        put("display", "flex")
        put("flex-direction", "column")
        put("flex", "1 1 auto")
        put("min-height", "0")
    }

    /** A cut corner: a triangle of the bezel over the screen's corner, the dark edge along its diagonal. */
    val screenCorner by rule {
        put("position", "absolute")
        put("width", "var(--kl-screen-cut)")
        put("height", "var(--kl-screen-cut)")
        put("z-index", "6")
        put("pointer-events", "none")
    }

    private fun screenCornerTowards(towards: String) =
        "linear-gradient($towards, var(--kl-screen-bezel) calc(50% - 0.75px), var(--kl-edge) calc(50% - 0.75px), " +
                "var(--kl-edge) calc(50% + 0.75px), transparent calc(50% + 0.75px))"

    val screenCornerTopLeft by rule {
        put("top", "0")
        put("left", "0")
        put("background", screenCornerTowards("to bottom right"))
    }

    val screenCornerTopRight by rule {
        put("top", "0")
        put("right", "0")
        put("background", screenCornerTowards("to bottom left"))
    }

    val screenCornerBottomLeft by rule {
        put("bottom", "0")
        put("left", "0")
        put("background", screenCornerTowards("to top right"))
    }

    val screenCornerBottomRight by rule {
        put("bottom", "0")
        put("right", "0")
        put("background", screenCornerTowards("to top left"))
    }

    // ── The checkbox ──────────────────────────────────────────────────────────

    /** The checkbox: its lamp and its label in one line, the whole of it clickable (KLANG-00349). */
    val checkbox by rule {
        put("position", "relative")
        put("display", "inline-flex")
        put("align-items", "center")
        put("gap", "8px")
        put("cursor", "pointer")
        put("user-select", "none")
        put("font-size", "13px")
        put("color", "var(--kl-ink-text)")
    }

    /** The checkbox's lamp: round, so its focus ring is round too. */
    val checkboxLamp by rule {
        put("display", "inline-flex")
        put("flex-shrink", "0")
        put("border-radius", "50%")
    }

    /** The hidden input under a lamp: the lamp shows the state itself; here only its keyboard focus. */
    val checkboxLampInput by rule {
        rule("&:focus-visible + *") {
            put("outline", "2px solid var(--kl-focus)")
            put("outline-offset", "3px")
        }
    }

    // ── The value knob and its LCD ────────────────────────────────────────────

    /** A knob and the LCD beside it, on one line. */
    val valueKnob by rule {
        put("display", "inline-flex")
        put("align-items", "center")
        put("gap", "8px")
        put("vertical-align", "middle")
    }

    /** An LCD: a sunken well in the look's ground, its digits in the LCD's ink with a faint glow. */
    val lcd by rule {
        put("display", "inline-flex")
        put("align-items", "center")
        put("justify-content", "flex-end")
        put("gap", "6px")
        put("box-sizing", "border-box")
        put("padding", "0 9px")
        put("line-height", "1")
        put("border", "1px solid var(--kl-edge)")
        put("border-radius", "var(--kl-radius)")
        put("background", "var(--kl-ground-sunken)")
        put("box-shadow", "inset 0 2px 4px rgba(0,0,0,0.6), 0 1px 0 rgba(255,255,255,0.06)")
        put("font-family", "monospace")
        put("color", "var(--kl-lcd-ink)")
        put("text-shadow", "0 0 6px color-mix(in srgb, var(--kl-lcd-ink) 55%, transparent)")
    }

    /** The reading: a click or Enter lets one type it. */
    val lcdValue by rule {
        put("font-size", "18px")
        put("text-align", "right")
        put("cursor", "text")
        put("outline", "none")
        put("border-radius", "2px")

        rule("&:focus-visible") {
            put("box-shadow", "0 0 0 1.5px var(--kl-focus)")
        }
    }

    /** The reading while it is typed: the same digits, in a field. */
    val lcdInput by rule {
        put("padding", "0")
        put("border", "none")
        put("outline", "none")
        put("background", "transparent")
        put("color", "inherit")
        put("font", "inherit")
        put("font-size", "18px")
        put("text-align", "right")
        put("text-shadow", "inherit")
    }

    /** The unit after the reading, small and printed; 1 px lower, on the digits' middle (the maintainer, by eye). */
    val lcdUnit by rule {
        put("position", "relative")
        put("top", "1px")
        put("font-size", "10px")
        put("letter-spacing", "0.14em")
        put("color", "var(--kl-ink-label)")
        put("text-shadow", "none")
    }

    // ── The spinner ───────────────────────────────────────────────────────────

    /** The spinner's box; its ring turns, the box stays. */
    val spinner by rule {
        put("display", "inline-block")
        put("flex-shrink", "0")
        put("vertical-align", "middle")

        rule("& > svg") {
            put("display", "block")
            put("width", "100%")
            put("height", "100%")
            put("animation", "kl-spin 0.9s linear infinite")
        }
    }

    // Both in the ink of the text beside it: the look's metal vanished on a silver button
    val spinnerTrack by rule {
        put("fill", "none")
        put("stroke", "color-mix(in srgb, currentColor 25%, transparent)")
    }

    val spinnerArc by rule {
        put("fill", "none")
        put("stroke", "currentColor")
        put("stroke-linecap", "round")
    }

    // ── The workbench ─────────────────────────────────────────────────────────

    val workbench by rule {
        put("display", "flex")
        put("width", "100%")
        put("height", "100%")
        put("overflow", "hidden")
        put("color", "var(--kl-ink-text)")
        put("background", "var(--kl-ground-app)")
    }

    /**
     * The column and everything right of it, side by side, framed on every side in a well's black (the maintainer,
     * 2026-11-02: "Give the entire page a black spacing", "a bit bigger", "Keep the frame, but make it narrow on the
     * bottom like the other sides", "a wider frame", then "Make the frame thin"), its inner corners cut as a screen's
     * are ("black triangular corner please"): the [screenCorner] triangles, in the frame's black.
     */
    val workbenchBody by rule {
        put("--kl-screen-cut", "14px")
        put("--kl-screen-bezel", "var(--kl-ground-sunken)")
        put("position", "relative")
        put("display", "flex")
        put("flex", "1")
        put("min-height", "0")
        put("overflow", "hidden")
        put("border", "4px solid var(--kl-ground-sunken)")
    }

    /** The column on the left: its content scrolls, the Motor under it never moves (KLANG-00334 item 25). */
    val workbenchColumn by rule {
        put("position", "relative")
        put("display", "flex")
        put("flex-direction", "column")
        put("width", "320px")
        put("flex-shrink", "0")
        put("background", "var(--kl-ground-deep)")
        put("border-right", "1px solid var(--kl-edge)")

        brushed()
    }

    /** The column above the Motor: its scroller. */
    val workbenchColumnArea by rule {
        put("position", "relative")
        put("display", "flex")
        put("flex-direction", "column")
        put("flex", "1")
        put("min-height", "0")
    }

    /**
     * The open menu's left side, over the column's waiting list (hidden under it): on the column's own brushed ground,
     * scrolling on its own.
     */
    val workbenchColumnSide by rule {
        put("position", "absolute")
        put("inset", "0")
        put("z-index", "5")
        put("overflow-y", "auto")
    }

    val workbenchColumnScroll by rule {
        put("flex", "1")
        put("min-height", "0")
        put("overflow-y", "auto")
    }

    val workbenchMotor by rule {
        put("flex-shrink", "0")
        put("border-top", "1px solid var(--kl-edge)")
    }

    /** Everything right of the column: the centre, a pinned tool window, the strip; the Motor's menu is laid over it. */
    val workbenchRight by rule {
        put("position", "relative")
        put("display", "flex")
        put("flex", "1")
        put("min-width", "0")
    }

    /**
     * The Motor's menu (KLANG-00403): over everything right of the column, edge to edge and square (the maintainer,
     * 2026-11-01: "cover 'everything' except the left side. No padding and not rounded corners"), above the tool windows
     * and the bottom panel flying in. Its panel fills it; taller than the window, the panel scrolls.
     */
    val workbenchDrawer by rule {
        put("position", "absolute")
        put("top", "0")
        put("right", "0")
        put("bottom", "0")
        put("left", "0")
        put("z-index", "30")
        put("display", "flex")
        put("flex-direction", "column")

        rule("& > .$panel") {
            put("flex", "1")
            put("min-height", "0")
            put("overflow-y", "auto")
            put("border-radius", "0")
            // Edge to edge, no drop shadow: it would fall over the column beside it
            put("box-shadow", "inset 0 1px 0 rgba(255,255,255,0.09)")
        }

        // Holding the focus only when it has no control of its own: a frame, not a control, so no ring
        rule("&:focus") {
            put("outline", "none")
        }
    }

    val workbenchCentre by rule {
        put("position", "relative")
        put("display", "flex")
        put("flex-direction", "column")
        put("flex", "1")
        put("min-width", "0")
    }

    /**
     * The page's area: it scrolls up and down; sideways it clips, as the app's old layout did, so a glow or a shadow
     * poking past the edge never adds a second scrollbar beside the editor's own.
     */
    val workbenchContent by rule {
        put("flex", "1")
        put("min-height", "0")
        put("overflow-x", "hidden")
        put("overflow-y", "auto")
    }

    /** The bottom panel pinned: docked under the content, which gives it room. Its height is the workbench's. */
    val workbenchBottomPinned by rule {
        put("display", "flex")
        put("flex-shrink", "0")
        // A kept height from a larger window never pushes the content (or the panel's own header) out of the room
        put("max-height", "calc(100% - ${KlangWorkbench.CONTENT_KEPT_PX.toInt()}px)")
        put("border-top", "1px solid var(--kl-edge)")

        // Its left edge meets the page's own (the line beside a right-side tool window would double it)
        rule("& .$toolWindow") {
            put("border-left", "none")
        }

        // Its header is its handle, to drag it higher or lower; the keys in it stay keys
        rule("& .$toolWindowHeader") {
            put("cursor", "ns-resize")
            put("touch-action", "none")
        }

        // Holding the focus as a frame, not a control: no ring
        rule("&:focus") {
            put("outline", "none")
        }
    }

    /** The bottom panel flying in over the content from below (KLANG-00349). Its height is the workbench's. */
    val workbenchBottomFloating by rule {
        put("position", "absolute")
        put("left", "0")
        put("right", "0")
        put("bottom", "0")
        put("z-index", "14")
        put("display", "flex")
        put("max-height", "calc(100% - ${KlangWorkbench.CONTENT_KEPT_PX.toInt()}px)")

        rule("& .$toolWindow") {
            put("border-left", "none")
        }

        rule("& .$toolWindowHeader") {
            put("cursor", "ns-resize")
            put("touch-action", "none")
        }

        rule("&:focus") {
            put("outline", "none")
        }
    }

    /** A tool window flying in over the content, from the strip's side. */
    val workbenchFloating by rule {
        put("position", "absolute")
        put("top", "0")
        put("right", "0")
        put("bottom", "0")
        // As wide as docked (the maintainer, 2026-10-28: "Take the docked width also for the fly in"; 380 before)
        put("width", "${TOOL_WINDOW_WIDTH_PX}px")
        put("z-index", "15")
        put("display", "flex")

        // Holding the focus as a frame, not a control: no ring
        rule("&:focus") {
            put("outline", "none")
        }
    }

    /** A pinned tool window, docked between the content and the strip. */
    val workbenchPinned by rule {
        put("display", "flex")
        put("width", "${TOOL_WINDOW_WIDTH_PX}px")
        put("flex-shrink", "0")

        // Holding the focus as a frame, not a control: no ring
        rule("&:focus") {
            put("outline", "none")
        }
    }

    val workbenchStrip by rule {
        put("display", "flex")
        put("flex-direction", "column")
        put("align-items", "center")
        put("gap", "6px")
        put("width", "48px")
        put("flex-shrink", "0")
        put("padding-top", "12px")
        put("background", "var(--kl-ground-deep)")
        put("border-left", "1px solid var(--kl-edge)")

        brushed()
    }

    /** The strip's foot: its keys, then the mark, down at the bottom (KLANG-00349). */
    val workbenchStripFoot by rule {
        put("display", "flex")
        put("flex-direction", "column")
        put("align-items", "center")
        put("gap", "18px")
        put("margin-top", "auto")
    }

    /** The keys at the strip's foot (fullscreen), spaced as the toggles above. */
    val workbenchStripKeys by rule {
        put("display", "flex")
        put("flex-direction", "column")
        put("align-items", "center")
        put("gap", "6px")
    }

    /** The maker's mark at the strip's foot, written upwards (KLANG-00334 items 22, 23). */
    val workbenchMark by rule {
        put("padding-bottom", "28px")

        // The mark, or the mark as a link home: the same lettering, no link colour, brighter under the mouse
        rule("& > span, & > a") {
            put("display", "block")
            put("writing-mode", "vertical-rl")
            put("transform", "rotate(180deg)")
            put("font-family", "monospace")
            put("font-weight", "700")
            put("font-size", "20px")
            put("letter-spacing", "0.12em")
            put("color", "var(--kl-ink-strong)")
            put("text-shadow", "0 0 5px var(--kl-edge)")
            put("white-space", "nowrap")
        }

        // An app may colour its links with !important (Klang's gold): the mark keeps its own ink against that
        rule("& > a, & > a:visited") {
            put("color", "var(--kl-ink-strong) !important")
            put("text-decoration", "none")
            put("cursor", "pointer")
        }

        rule("& > a:hover") {
            put("color", "var(--kl-ink-text) !important")
        }
    }

    // ── Gauges ────────────────────────────────────────────────────────────────

    /** The rim's width, as the inset of the face and of the glass over it, one value for both. */
    private const val GAUGE_RIM = "5%"

    /** The rim: silver, lit from above. The glow's blur comes per gauge as `--gauge-blur`, its size times a share. */
    val gauge by rule {
        put("position", "relative")
        put("display", "inline-block")
        put("flex-shrink", "0")
        put("border-radius", "50%")
        put("background", "linear-gradient(180deg, var(--kl-metal-light), var(--kl-metal-mid) 55%, var(--kl-metal-dark))")
        put("box-shadow", "0 2px 4px rgba(0,0,0,0.6)")
        // An instrument, not text: its reading and its icon cannot be selected (the maintainer, 2026-10-27)
        put("user-select", "none")
        put("-webkit-user-select", "none")
    }

    val gaugeOnline by rule {
        put(
            "box-shadow",
            "0 2px 4px rgba(0,0,0,0.6), 0 0 var(--gauge-blur) color-mix(in srgb, var(--kl-gauge-glow) 60%, transparent)"
        )
    }

    val gaugeHot by rule {
        put(
            "box-shadow",
            "0 2px 4px rgba(0,0,0,0.6), 0 0 var(--gauge-blur) color-mix(in srgb, var(--kl-lamp-critical) 70%, transparent)"
        )
    }

    /** The face, inside the rim: the rim's width is this inset (the maintainer, 2026-10-26: "slightly thicker", 6% too thick). */
    val gaugeFace by rule {
        put("position", "absolute")
        put("inset", GAUGE_RIM)
        put("border-radius", "50%")
        put(
            "background",
            "radial-gradient(circle at 50% 35%, color-mix(in srgb, var(--kl-ground-sunken), white 7%) 0%," +
                    " var(--kl-ground-sunken) 70%)"
        )
        put("box-shadow", "inset 0 2px 4px rgba(0,0,0,0.8)")
    }

    /**
     * Domed glass over the face, ever so slightly (the maintainer, 2026-10-26): light caught at the top of the dome
     * ([gaugeGlassReflection]), and the glass a touch darker where it curves down into the rim. Above the scale and the
     * value, it takes no clicks; it clips its reflection.
     */
    val gaugeGlass by rule {
        put("position", "absolute")
        put("inset", GAUGE_RIM)
        put("border-radius", "50%")
        put("overflow", "hidden")
        put("pointer-events", "none")

        // The glass's curve over its reflection, as it lay over the reflection painted into the glass before: an inset
        // shadow paints under the element's children, so it is drawn on a layer after them
        rule("&::after") {
            put("content", "''")
            put("position", "absolute")
            put("inset", "0")
            put("border-radius", "inherit")
            put("pointer-events", "none")
            put("box-shadow", "inset 0 -2px 5px rgba(255,255,255,0.05), inset 0 0 6px rgba(0,0,0,0.35)")
        }
    }

    /**
     * The light caught in the glass, a layer of its own, so a lamp can move it by its transform and its opacity alone
     * without painting the gauge again (the maintainer, 2026-11-02: "the light reflection of the gauges must also
     * follow the swinging lamp"; the visuals' board and the start page move it as they move their domes' highlights).
     * Painted at twice its old brightness and shown at half: at rest it is the reflection of old, and a brighter lamp
     * can brighten it to twice that. Larger than the glass by [KlangGauge.REFLECTION_MARGIN] all round, so moved it
     * never uncovers the glass; its gradient is the old one's (ellipse 72% by 46% of the glass, at 50% and 16%) in
     * the layer's own per cent. Its own compositing layer: moved, it paints nothing again.
     */
    val gaugeGlassReflection by rule {
        val size = KlangGauge.REFLECTION_SIZE
        val margin = KlangGauge.REFLECTION_MARGIN

        fun share(glassPct: Double): String = "${glassPct * 100.0 / size}%"

        put("position", "absolute")
        put("inset", "-$margin%")
        put("opacity", "0.5")
        put("will-change", "transform, opacity")
        put(
            "background",
            "radial-gradient(ellipse ${share(72.0)} ${share(46.0)} at 50% ${share(margin + 16.0)}, " +
                    "rgba(255,255,255,0.26), rgba(255,255,255,0.06) 62%, transparent 76%)"
        )
    }

    val gaugeScale by rule {
        put("position", "absolute")
        put("inset", "0")
        put("width", "100%")
        put("height", "100%")
        put("pointer-events", "none")
    }

    val gaugeTick by rule {
        put("stroke", "var(--kl-gauge-tick)")
    }

    val gaugeTickHot by rule {
        put("stroke", "var(--kl-lamp-critical)")
    }

    val gaugeTrack by rule {
        put("fill", "none")
        put("stroke", "color-mix(in srgb, var(--kl-gauge-tick) 25%, transparent)")
        put("stroke-linecap", "round")
    }

    val gaugeArc by rule {
        put("fill", "none")
        put("stroke", "var(--kl-gauge-arc)")
        put("stroke-linecap", "round")
    }

    val gaugeArcHot by rule {
        put("stroke", "var(--kl-lamp-critical)")
    }

    val gaugeNeedle by rule {
        put("stroke", "var(--kl-ink-strong)")
        put("stroke-linecap", "round")
    }

    val gaugeHub by rule {
        put("fill", "var(--kl-metal-mid)")
        put("stroke", "var(--kl-edge)")
    }

    /** The icon or the text label, between the arc and the hub, clear of both (maintainer, 2026-10-25). Dim while offline. */
    val gaugeMark by rule {
        put("position", "absolute")
        put("left", "50%")
        put("top", "32%")
        put("transform", "translate(-50%, -50%)")
        put("margin", "0")
        put("color", "var(--kl-ink-label)")
        put("font-weight", "700")
        put("letter-spacing", "0.08em")
        put("white-space", "nowrap")
    }

    val gaugeMarkLit by rule {
        put("color", "var(--kl-gauge-arc)")
        put("text-shadow", "0 0 7px color-mix(in srgb, var(--kl-gauge-arc) 60%, transparent)")
    }

    val gaugeMarkHot by rule {
        put("color", "var(--kl-lamp-critical)")
        put("text-shadow", "0 0 7px color-mix(in srgb, var(--kl-lamp-critical) 60%, transparent)")
    }

    val gaugeValue by rule {
        put("position", "absolute")
        put("left", "0")
        put("right", "0")
        put("bottom", "20%")
        put("text-align", "center")
        put("font-family", "monospace")
        put("color", "var(--kl-ink-text)")
    }

    // ── Knobs ─────────────────────────────────────────────────────────────────

    /** Tight, to save space (maintainer, 2026-10-25): the label sits against the dial, the value right under the dial. */
    val knob by rule {
        put("display", "inline-flex")
        put("flex-direction", "column")
        put("align-items", "center")
        put("gap", "4px")
        put("user-select", "none")
    }

    /** What sits under the dial: the label when it goes below, then the value, 2 px apart. */
    val knobCaption by rule {
        put("display", "flex")
        put("flex-direction", "column")
        put("align-items", "center")
        put("gap", "2px")
    }

    val knobLabel by rule {
        put("font-size", "10px")
        put("line-height", "1")
        put("font-weight", "700")
        put("letter-spacing", "0.14em")
        put("color", "var(--kl-ink-text)")
    }

    val knobValue by rule {
        put("font-family", "monospace")
        put("font-size", "11px")
        put("line-height", "1")
        put("color", "var(--kl-ink-label)")
    }

    /** The part that is dragged and takes the keys; a finger's drag turns it instead of scrolling the page. */
    val knobDial by rule {
        put("position", "relative")
        put("flex-shrink", "0")
        put("border-radius", "50%")
        // Four ways: a drag up, down, left or right turns it (the knob at the screen's top edge turns sideways)
        put("cursor", "move")
        put("touch-action", "none")
        put("outline", "none")

        // A thin ring on the dial's edge, clear of the label (maintainer, 2026-10-25). A shadow ring, because Chrome
        // draws a round outline without anti-aliasing (it looked pixelated); the transparent outline still shows in
        // forced-colours modes, which drop shadows. On any focus, not only `:focus-visible`: the focused knob is the
        // one the arrows turn, and the browser's `:focus-visible` hid the ring after a click that followed a button's
        // (the maintainer, 2026-10-25)
        rule("&:focus") {
            put("box-shadow", "0 0 0 1.5px var(--kl-focus)")
            put("outline", "1px solid transparent")
        }
    }

    val knobTrack by rule {
        put("fill", "none")
        put("stroke", "color-mix(in srgb, var(--kl-ink-label) 30%, transparent)")
        put("stroke-linecap", "round")
    }

    val knobArc by rule {
        put("fill", "none")
        put("stroke", "var(--kl-knob-arc)")
        put("stroke-linecap", "round")
    }

    /** The cap, domed and lit from above; its inset comes per knob (with or without the ring). */
    val knobCap by rule {
        put("position", "absolute")
        put("border-radius", "50%")
        put("border", "1px solid var(--kl-edge)")
        put("box-shadow", "0 3px 5px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.18)")
    }

    val knobCapDark by rule {
        // The sheen only: a small dark cap holds three or four rings of 4 px, which read as a blur (the maintainer,
        // 2026-10-28: "the small knobs now appear blurred")
        put(
            "background",
            turned(sheen = 0.12, rings = 0.0) +
                ", radial-gradient(circle at 42% 30%, var(--kl-cap-light) 0%, var(--kl-cap-dark) 100%)",
        )
        grain()
        grainTurns()
    }

    val knobCapSilver by rule {
        put(
            "background",
            // Darker than a first try, the metal's dark over its outer third, as the gauges' rims on the start page's next
            // screen (the maintainer, 2026-10-28: "the button is too bright now in general... it should match the next
            // screen a bit more")
            turned(sheen = 0.18, rings = 0.12) +
                ", radial-gradient(circle at 42% 28%, var(--kl-metal-light) 0%, var(--kl-metal-mid) 30%, " +
                "var(--kl-metal-dark) 78%, var(--kl-metal-dark) 100%)",
        )
        grain()
        grainTurns()
    }

    /**
     * A turned finish over a round cap's metal, as a part made on a lathe (the maintainer, 2026-10-28: the gold key
     * "looks like an old plastic that once was white and is now turning yellow"; a soft dome of light is how plastic
     * shines): fine concentric rings, and the light on them in two broad wedges, bright at the upper left and the lower
     * right, at [sheen] and [rings] strength (a first 0.42 and 0.07 on silver was "a bit too much"). The
     * rings are round, so the wedges stay where the light is while the cap turns; only the grain turns with it.
     */
    private fun turned(sheen: Double, rings: Double): String {
        val light = "rgba(255, 255, 255, $sheen)"
        val clear = "rgba(255, 255, 255, 0)"

        // Light only: dark wedges met in a hard cross at the middle (the maintainer, 2026-10-28: "the dark areas in
        // the center are weird"); two broad bright ones, soft at their edges
        // The second wedge wraps round past 0: the cone starts at its 40 % so the turn has no seam (a stop past 360
        // left a line at the top)
        val wrap = "rgba(255, 255, 255, ${sheen * 0.4})"
        val wedges = "conic-gradient(from 0deg, $wrap 0deg, $clear 30deg, $clear 60deg, $light 135deg, $clear 210deg, " +
            "$clear 240deg, $light 315deg, $wrap 360deg)"

        if (rings <= 0.0) {
            return wedges
        }

        return listOf(
            wedges,
            // Rings of 4 px, light fading to dark and back: 1 px rings aliased into a fine moire at 1x (the maintainer,
            // 2026-10-28: "make the rings less but thicker each")
            "repeating-radial-gradient(circle at 50% 50%, rgba(255, 255, 255, $rings) 0, rgba(0, 0, 0, $rings) 2px, " +
                "rgba(255, 255, 255, $rings) 4px)",
        ).joinToString(", ")
    }

    /**
     * A turning cap's grain turns with it, by the cap's `--kl-knob-turn`, while its light stays where it is (the
     * maintainer, 2026-10-28: "the turning knobs must also turn the grain").
     */
    private fun CssBuilder.grainTurns() {
        rule("&::before") {
            put("transform", "rotate(var(--kl-knob-turn, 0deg))")
        }
    }

    /** The turning layer; its rotation comes per knob. */
    val knobTurn by rule {
        put("position", "absolute")
        put("inset", "0")
        put("pointer-events", "none")
    }

    /** The pointer line, printed in the cap's ink. */
    val knobPointer by rule {
        put("position", "absolute")
        put("left", "50%")
        put("top", "8%")
        put("width", "8%")
        // Ends well before the middle (the maintainer, 2026-10-28: "it goes too far into the center"; it reached 42 %)
        put("height", "26%")
        put("transform", "translateX(-50%)")
        put("border-radius", "1px")
        put("background", "var(--kl-ink-strong)")
        // Cut into the cap, not painted on (the maintainer, 2026-10-28: "should look like mechanically created"), with
        // hard edges like the gauges' needles (2026-11-02: "needs a sharper edge ... too soft"): its upper wall in
        // shadow and a crisp dark rim, nothing blurred
        put("box-shadow", "inset 0 1px 0 rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(0, 0, 0, 0.6)")
    }

    /** The pointer as a dot at the rim, when an icon sits on the cap. */
    val knobDot by rule {
        put("position", "absolute")
        put("left", "50%")
        put("top", "7%")
        put("width", "13%")
        put("height", "13%")
        put("transform", "translateX(-50%)")
        put("border-radius", "50%")
        put("background", "var(--kl-ink-strong)")
    }

    val knobIcon by rule {
        put("position", "absolute")
        put("left", "50%")
        put("top", "50%")
        put("transform", "translate(-50%, -50%)")
        put("margin", "0")
        put("color", "var(--kl-ink-strong)")
    }

    /** The silver cap's ink for its pointer or its dot. */
    val knobMarkSilver by rule {
        // A groove in the metal: a flat floor in the metal's ink, its walls in an even shadow, its lip catching the
        // light; shaded across its width it read as a rod lying on the cap (the maintainer, 2026-10-28: "Strange")
        put("background", "var(--kl-metal-ink)")
        put("box-shadow", "inset 0 0 2px 1px rgba(0, 0, 0, 0.75), 0 0 0 1px rgba(255, 255, 255, 0.35)")
    }

    /** The silver cap's ink for its icon. */
    val knobIconSilver by rule {
        put("color", "var(--kl-metal-ink)")
    }

    /**
     * A line of text rolling through its display from the right edge out at the left, for ever (the now-playing line
     * while no song plays, the maintainer, 2026-11-02). Its parent clips it (`overflow: hidden`); still, at the left,
     * under "reduce motion".
     */
    val marquee by rule {
        put("display", "inline-block")
        put("padding-left", "100%")
        put("white-space", "nowrap")
        put("animation", "kl-marquee 18s linear infinite")
    }

    // ── Motion ────────────────────────────────────────────────────────────────

    /**
     * The pulses, and their stillness under "reduce motion": a lit ring instead of a pulsing one.
     *
     * This sheet mounts before the main one, which then wins a tie, so the stillness rules carry one class more than
     * the rules they override.
     */
    @Suppress("unused")
    private val motion = RawStyleSheet(
        css = """
            @keyframes kl-beacon {
                0%, 100% { box-shadow: 0 0 0 0 transparent; }
                50% {
                    box-shadow: 0 0 0 5px color-mix(in srgb, var(--kl-lamp-try) 35%, transparent),
                        0 0 18px color-mix(in srgb, var(--kl-lamp-try) 65%, transparent);
                }
            }
            @keyframes kl-fly-in {
                from { transform: translateX(60px); opacity: 0; }
                to { transform: none; opacity: 1; }
            }
            @keyframes kl-fly-up {
                from { transform: translateY(60px); opacity: 0; }
                to { transform: none; opacity: 1; }
            }
            @keyframes kl-spin {
                to { transform: rotate(360deg); }
            }
            @keyframes kl-spin-still {
                0%, 100% { opacity: 0.45; }
                50% { opacity: 1; }
            }
            @keyframes kl-marquee {
                from { transform: translateX(0); }
                to { transform: translateX(-100%); }
            }
            @keyframes kl-lamp-pulse {
                0%, 100% { filter: brightness(0.8); }
                50% { filter: brightness(1.3); }
            }
            @media (prefers-reduced-motion: reduce) {
                .$button.$beacon::after { animation: none; box-shadow: 0 0 0 2px var(--kl-lamp-try); }
                .$lamp.$lampTry { animation: none; }
                .$toolWindow.$toolWindowFloating { animation: none; }
                .$toolWindow.$toolWindowFloatingBottom { animation: none; }
                .$ignitionCap.$beacon::after { animation: none; box-shadow: 0 0 0 2px var(--kl-lamp-try); }
                .$iconToggle.$beacon::after { animation: none; box-shadow: 0 0 0 2px var(--kl-lamp-try); }
                .$quietKey.$beacon::after { animation: none; box-shadow: 0 0 0 2px var(--kl-lamp-try); }
                .$knobTurn.$ignitionTurn { transition: none; }
                .$spinner.$spinner > svg { animation: kl-spin-still 1.6s ease-in-out infinite; }
                .$marquee.$marquee { animation: none; padding-left: 0; }
            }
        """.trimIndent()
    )
}

/**
 * The look of a count badge, on an icon toggle's corner and after a tab's title alike: a small lit error lamp with a
 * number in it, in the lamp's own colours, no roles of its own (the maintainer, 2026-11-03: "I would probably not
 * introduce new colors but use the one the lamp use"). One place, so both badges change together. A parent may set
 * `--kl-badge-lamp` to another lamp's colour (the look editor shows it in each); unset, it is the error lamp.
 */
private val COUNT_BADGE = listOf(
    "min-width" to "16px",
    "height" to "16px",
    "padding" to "0 4px",
    "box-sizing" to "border-box",
    "border-radius" to "8px",
    "background" to "var(--kl-badge-lamp, var(--kl-lamp-critical))",
    "box-shadow" to "0 0 6px var(--kl-badge-lamp, var(--kl-lamp-critical))",
    // The number engraved in the lamp, as the menu tiles' icons are raised from their caps (the maintainer, 2026-11-03:
    // "a similar raised or engraved style as the big icons on the main buttons in the menu"): cut in, it is darker than
    // the lamp, its lower edge catching the light; white was too weak on the coral ("The contrast is not good")
    "color" to "color-mix(in srgb, var(--kl-badge-lamp, var(--kl-lamp-critical)), black 68%)",
    "text-shadow" to "0 1px 0 color-mix(in srgb, var(--kl-badge-lamp, var(--kl-lamp-critical)), white 40%), " +
        "0 -1px 0 rgba(0, 0, 0, 0.3)",
    "font-family" to "inherit",
    "font-size" to "10px",
    "font-weight" to "700",
    "line-height" to "16px",
    "text-align" to "center",
    "text-transform" to "none",
    "pointer-events" to "none",
)
